import React from 'react';
import {
	KNOWN_COLUMN_TYPES,
	COLUMN_ID_CHECKBOX,
	COLUMN_ID_MERGED_COLUMN,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type { IntlShape } from '@atlassian/jira-intl';
import type { Column } from '../../types.tsx';
import { getColumnConfig } from '../get-column-config/index.tsx';
import ExpandIcon from '../../ui/header-cell/expand/index.tsx';
import messages from './messages.tsx';

const getColumnTitle = (
	mergedColumns: Column[],
	baseField: Column | null,
	formatMessage: IntlShape['formatMessage'],
) => {
	if (mergedColumns.length === 0) {
		return '';
	}
	if (mergedColumns.length === 1 && baseField) {
		return baseField.title;
	}
	return formatMessage(messages.columnHeaderMergedColumn);
};

export const getMergedColumnConfig = (
	columns: Column[],
	isIssueHierarchyEnabled: boolean,
	formatMessage: IntlShape['formatMessage'],
) => {
	const filteredColumns: Column[] = [];
	let mergedColumns: Column[] = [];

	let issueKeyColumn: Column | null = null;
	let issueTypeColumn: Column | null = null;
	let summaryColumn: Column | null = null;

	let isCheckboxColumnEnabled = false;

	// Filter out the issue key, issue type, and summary columns
	columns.forEach((column) => {
		if (column.type === KNOWN_COLUMN_TYPES.ISSUE_KEY) {
			issueKeyColumn = column;
		} else if (column.type === KNOWN_COLUMN_TYPES.ISSUE_TYPE) {
			issueTypeColumn = column;
		} else if (column.type === KNOWN_COLUMN_TYPES.SUMMARY) {
			summaryColumn = column;
		} else {
			filteredColumns.push(column);
		}
		if (column.id === COLUMN_ID_CHECKBOX) {
			isCheckboxColumnEnabled = true;
		}
	});

	// Always preserve this order: issue type, key, summary
	mergedColumns = [issueTypeColumn, issueKeyColumn, summaryColumn].filter(Boolean);

	// If there is no issue key, issue type, or summary column, we show only expand & child issue create icons
	const baseField = summaryColumn ?? issueKeyColumn ?? issueTypeColumn ?? null;

	// return original columns if hierarchy is OFF and merged fields are OFF
	if (!mergedColumns.length && !isIssueHierarchyEnabled) {
		return columns;
	}

	const columnConfig =
		baseField ?? getColumnConfig(formatMessage)[KNOWN_COLUMN_TYPES.MERGED_COLUMN]();

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const resizeColumnId = baseField ? (baseField as Column).id : undefined;

	const icon = isIssueHierarchyEnabled ? <ExpandIcon /> : undefined;

	const mergedColumn = {
		jqlTerm: '',
		fieldTypeDisplayName: '',
		...columnConfig,
		id: COLUMN_ID_MERGED_COLUMN,
		type: KNOWN_COLUMN_TYPES.MERGED_COLUMN,
		title: getColumnTitle(mergedColumns, baseField, formatMessage),
		isSortable: false,
		mergedColumns,
		resizeColumnId,
		customHeader: baseField ? undefined : icon,
		icon,
	};

	// insert the merged column after the checkbox column if it exists
	const mergedColumnIndex = isCheckboxColumnEnabled ? 1 : 0;

	return [
		...filteredColumns.slice(0, mergedColumnIndex),
		mergedColumn,
		...filteredColumns.slice(mergedColumnIndex),
	];
};
